/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import FileSystemType from '../file/system/file-system-type';
import LogicalUnit from '../logical-unit/logical-unit';

// TODO: Rozhraní LogicalUnitMapper není nikde dokumentováno!
/**
 * Resolver pro interní cestu.
 * @implements {LogicalUnitMapper}
 */
class SimpleLogicalUnitMapper {
  /**
   * Funkce generující názvy pro logické jednotky.
   * @name LogicalUnitNameGenerator
   * @function
   * @returns {string} Název logické jednotky.
   */

  // TODO: Refaktorovat logicalUnitNameGenerator na defaultní core implementaci a řešit změnu jen pro testy, místo duplikované platformově specifické DI.
  /**
   * Vytvoří instanci.
   * @param {FileSystemManager} fileSystemManager Správce souborového systému.
   * @param {FileSystemPathFactory} fileSystemPathFactory Továrna na cesty v souborovém systému.
   * @param {LogicalUnitNameGenerator} logicalUnitNameGenerator Generator názvů pro logické jednotky
   * @returns {SimpleLogicalUnitMapper} Nová instance.
   */
  constructor(
    fileSystemManager,
    fileSystemPathFactory,
    logicalUnitNameGenerator
  ) {
    this.fileSystemManager = fileSystemManager;
    this.fileSystemPathFactory = fileSystemPathFactory;
    this.logicalUnitNameGenerator = logicalUnitNameGenerator;
    // TODO: Předělat na konstantu na úrovni souboru.
    this.externalDeviceIdFilename = 'player-lighter-external';

    // TODO: Nějaké dynamická aktualizace mapperu připojených jednotek,
    // aby bylo možné je přidávat i v průběhu.
    // TODO: Refaktorovat totálně podivné asynchronní ukládání a in-place měnění hodnoty v Promise!
    // TODO: Promise<Map<string, FileSystemUnit>>, kde klíč je LogicalUnit.getName()
    this.logicalNameToFileSystemUnitMapPromise = new Promise((resolve) =>
      resolve(
        this.fileSystemManager.getAllUnits().then((fileSystemUnits) =>
          fileSystemUnits.reduce(
            (acc, fileSystemUnit) =>
              acc.then(async (logicalToPhysicalMap) => {
                // Interní jednotky mají pro zpětnou kompatibilitu logické jméno stejné jako fyzické.
                let logicalName = fileSystemUnit.getName();
                if (fileSystemUnit.getType() !== FileSystemType.Internal) {
                  try {
                    const externalDeviceIdPath =
                      this.fileSystemPathFactory.fromValue(
                        `/${fileSystemUnit.getName()}/${
                          this.externalDeviceIdFilename
                        }`
                      );
                    if (
                      !(await this.fileSystemManager.hasFile(
                        externalDeviceIdPath
                      ))
                    ) {
                      return logicalToPhysicalMap;
                    }

                    // TODO: Podivný název, přejmenovat.
                    // TODO: Co když někdo dá \n a jiné všivé znaky do souboru.
                    const externalDeviceIdContent =
                      await this.fileSystemManager.readFile(
                        externalDeviceIdPath
                      );
                    // Externí zařízení není oštítkováno
                    if (!externalDeviceIdContent) {
                      return logicalToPhysicalMap;
                    }
                    logicalName = externalDeviceIdContent;
                  } catch (err) {
                    return logicalToPhysicalMap;
                  }
                }

                logicalToPhysicalMap[logicalName] = fileSystemUnit;
                return logicalToPhysicalMap;
              }),
            Promise.resolve({})
          )
        )
      )
    );
  }

  // TODO: Přejmenovat na toLogicalUnit?
  // TODO: Dokumentovat chování při očekávaných chybách jako nepřítomnost fyzické jednotky.
  /**
   * Namapuje fyzickou jednotku na logickou.
   * @param {FileSystemUnit} fileSystemUnit Fyzická jednotka.
   * @returns {Promise.<LogicalUnit>} Logická jednotka.
   */
  toLogical(fileSystemUnit) {
    return new Promise((resolve) =>
      resolve(
        this.logicalNameToFileSystemUnitMapPromise.then(
          async (logicalNameToFileSystemUnitMap) => {
            const fileSystemUnitName = fileSystemUnit.getName();
            // Pokud ještě nebyla externí jednotka namapovaná
            // TODO: Neoptimální O(n) hledání - nahradit za this.PhysicalToLogicalMap.
            let logicalName = Object.keys(logicalNameToFileSystemUnitMap).find(
              (candidateLogicalName) =>
                logicalNameToFileSystemUnitMap[
                  candidateLogicalName
                ].getName() === fileSystemUnitName
            );
            if (!logicalName) {
              // Interní jednotky mají pro zpětnou kompatibilitu logické jméno stejné jako fyzické.
              logicalName = fileSystemUnit.getName();
              if (fileSystemUnit.getType() !== FileSystemType.Internal) {
                logicalName = this.logicalUnitNameGenerator();
                const externalDeviceIdPath =
                  this.fileSystemPathFactory.fromValue(
                    `/${fileSystemUnit.getName()}/${
                      this.externalDeviceIdFilename
                    }`
                  );
                // Oštítkování externího zařízení
                await this.fileSystemManager.writeFile(
                  externalDeviceIdPath,
                  logicalName
                );
              }

              logicalNameToFileSystemUnitMap[logicalName] = fileSystemUnit;
            }
            // TODO: Není optimální, stále alokuje.
            return new LogicalUnit(logicalName);
          }
        )
      )
    );
  }

  // TODO: Dokumentovat chování při očekávaných chybách jako nepřítomnost fyzické jednotky.
  /**
   * Namapuje logickou jednotku na fyzickou.
   * @param {LogicalUnit} logicalUnit Logická jednotka.
   * @returns {Promise.<FileSystemUnit>} Fyzická jednotka.
   */
  toFileSystemUnit(logicalUnit) {
    return new Promise((resolve) =>
      resolve(
        this.logicalNameToFileSystemUnitMapPromise.then(
          (logicalToPhysicalMap) => {
            const logicalName = logicalUnit.getName();
            const fileSystemUnit = logicalToPhysicalMap[logicalName];
            if (!fileSystemUnit) {
              throw new Error(
                `unable to resolve logical name "${logicalName}"`
              );
            }
            return fileSystemUnit;
          }
        )
      )
    );
  }
}

export default SimpleLogicalUnitMapper;
